import { FC, useCallback, useContext, useEffect, useMemo } from 'react';

import {
  ColSize,
  InputCurrencyForm,
  InputForm,
  InputNumberForm,
  RowSize,
  SelectForm,
} from '../../../../../../../../../../../shared/components';
import {
  SectionQuotation,
  StepOneFormContext,
  QUOTATION_BUTTON,
  QuotationInfoContext
} from '../../../../../../../shared';

import { StepFourFormContext } from '../../../../contexts/StepFourForm';
import { StepTwoFormContext } from '../../../../../StepTwo/contexts/StepTwoForm';

import { ValidateForm } from '../../../../../../../../../../../shared/utils';
import { WizardDataFormContext } from '../../../../../../Wizard/contexts/WizardDataForm';

interface GeneralDataProps {
  tabKey: number;
}

export const GeneralData: FC<GeneralDataProps> = ({ tabKey }) => {
  const validateForm = new ValidateForm();

  const { stepTwoForm } = useContext(WizardDataFormContext);

  const { tabsSectionForm, useList, isLoadingUseList, collateralCreditors } =
    useContext(StepFourFormContext);
  const {
    yearList,
    brandsByYear,
    modelsByBrandAndYear,
    versionsByYearBrandAndModel,
  } = useContext(StepTwoFormContext);

  const { stepOneForm } = useContext(StepOneFormContext);

  const yearFieldName = `vehicleYear-tab-${tabKey}`;
  const yearSelected = stepTwoForm?.getFieldValue(yearFieldName);

  const brandFieldName = `brand-tab-${tabKey}`;
  const brandSelected = stepTwoForm?.getFieldValue(brandFieldName);

  const modelFieldName = `model-tab-${tabKey}`;
  const modelSelected = stepTwoForm?.getFieldValue(modelFieldName);

  const versionFieldName = `version-tab-${tabKey}`;
  const versionSelected = stepTwoForm?.getFieldValue(versionFieldName);

  const hasGNCFieldName = `gnc-tab-${tabKey}`;
  const gncValueFieldName = `gncValue-tab-${tabKey}`;

  const originFieldName = `origin-tab-${tabKey}`;
  const originFieldValue = stepTwoForm?.getFieldValue(originFieldName);

  const zoneFieldName = `zone-tab-${tabKey}`;
  const zoneFieldValue = stepTwoForm?.getFieldValue(zoneFieldName);

  const infoValueFieldName = `infoValueCar-tab-${tabKey}`;
  const infoValueCar = stepTwoForm?.getFieldValue(infoValueFieldName);

  const chassisFieldName = `chassisNumber-tab-${tabKey}`;
  const chassisNumberValue = stepTwoForm?.getFieldValue(chassisFieldName);

  const engineNumberFieldName = `engineNumber-tab-${tabKey}`;
  const engineNumberValue = stepTwoForm?.getFieldValue(engineNumberFieldName);

  const patentFieldName = `patent-tab-${tabKey}`;
  const patentValue = stepTwoForm?.getFieldValue(patentFieldName);

  const useFieldName= `use-tab-${tabKey}`;
  const useValue = stepTwoForm?.getFieldValue(useFieldName);

  const driverNameFieldName= `driver-tab-${tabKey}`;
  const driverNameValue = stepTwoForm?.getFieldValue(driverNameFieldName);
  
  const brands = useMemo(
    () => brandsByYear.find((el) => el.year === yearSelected)?.brands ?? [],
    [brandsByYear, yearSelected]
  );

  const models = useMemo(
    () =>
      modelsByBrandAndYear.find(
        (el) => el.year === yearSelected && el.brand === brandSelected
      )?.models ?? [],
    [modelsByBrandAndYear, yearSelected, brandSelected]
  );

  const versions = useMemo(
    () =>
      versionsByYearBrandAndModel.find(
        (el) =>
          String(el.year) === String(yearSelected) &&
          String(el.brand) === String(brandSelected) &&
          String(el.model) === String(modelSelected)
      )?.versionOptions ?? [],
    [versionsByYearBrandAndModel, yearSelected, modelSelected, brandSelected]
  );

  const setFields = useCallback(() => {
    if (stepTwoForm?.getFieldValue(hasGNCFieldName)) {
      const gncValue = stepTwoForm?.getFieldValue(gncValueFieldName);
      tabsSectionForm?.setFieldValue(gncValueFieldName, gncValue);
    }

    tabsSectionForm?.setFieldValue(yearFieldName, yearSelected);

    tabsSectionForm?.setFieldValue(brandFieldName, brandSelected);

    tabsSectionForm?.setFieldValue(modelFieldName, modelSelected);

    tabsSectionForm?.setFieldValue(versionFieldName, versionSelected);

    tabsSectionForm?.setFieldValue(originFieldName, originFieldValue);

    tabsSectionForm?.setFieldValue(zoneFieldName, zoneFieldValue);

    tabsSectionForm?.setFieldValue(infoValueFieldName, infoValueCar);
  }, [
    tabsSectionForm,
    stepTwoForm,
    yearSelected,
    brandSelected,
    modelSelected,
    versionSelected,
    yearFieldName,
    brandFieldName,
    modelFieldName,
    versionFieldName,
    hasGNCFieldName,
    originFieldName,
    originFieldValue,
    zoneFieldName,
    zoneFieldValue,
    infoValueFieldName,
    infoValueCar,
    gncValueFieldName,
    chassisNumberValue,
    engineNumberValue,
    patentValue,
    useValue,
  ]);

  useEffect(() => {
    setFields();    
  }, [setFields]);
  

  return (
    <SectionQuotation level='h3' title='Datos generales' hasCard={false}>
      <RowSize>
        <ColSize size={25}>
          <InputForm
            formProps={{
              label: 'Nombre Del Conductor:',
              name: `driver-name-tab-${tabKey}`,
              initialValue: stepOneForm?.fullName || driverNameValue,
              rules: validateForm.generateRules({
                label: 'nombre del conductor',
                rules: ['required'],
              }),
            }}
          />
        </ColSize>
        <ColSize size={25}>
          <InputForm
            formProps={{
              label: 'Conductor:',
              name: `driver-tab-${tabKey}`,
              initialValue: 'PRINCIPAL',
            }}
            inputProps={{
              disabled: true,
            }}
          />
        </ColSize>
        <ColSize size={25}>
          <SelectForm
            formProps={{
              label: 'Uso:',
              name: `use-tab-${tabKey}`,
              rules: validateForm.generateRules({
                label: 'uso',
                rules: ['required'],
              }),
              initialValue: useValue ?? undefined,
            }}
            selectProps={{
              disabled: ((useList.length > 0 && isLoadingUseList) || !!useValue),
              loading: isLoadingUseList,
              options: useList,
            }}
          />
        </ColSize>
        <ColSize size={25}>
          <InputCurrencyForm
            formProps={{
              label: 'Valor del GNC:',
              name: gncValueFieldName,
            }}
            inputCurrencyProps={{ disabled: true }}
          />
        </ColSize>
        <ColSize size={25}>
          <SelectForm
            formProps={{
              label: 'Año:',
              name: yearFieldName,
            }}
            selectProps={{
              disabled: true,
              options: yearList,
            }}
          />
        </ColSize>
        <ColSize size={25}>
          <SelectForm
            formProps={{
              label: 'Marca:',
              name: brandFieldName,
            }}
            selectProps={{
              disabled: true,
              options: brands,
            }}
          />
        </ColSize>
        <ColSize size={25}>
          <SelectForm
            formProps={{
              label: 'Modelo:',
              name: modelFieldName,
            }}
            selectProps={{
              disabled: true,
              options: models,
            }}
          />
        </ColSize>
        <ColSize size={25}>
          <SelectForm
            formProps={{
              label: 'Versión:',
              name: versionFieldName,
            }}
            selectProps={{
              disabled: true,
              options: versions,
            }}
          />
        </ColSize>
        <ColSize size={25}>
          <InputForm
            formProps={{
              label: 'Origen:',
              name: originFieldName,
            }}
            inputProps={{
              disabled: true,
            }}
          />
        </ColSize>
        <ColSize size={25}>
          <InputNumberForm
            formProps={{
              label: 'Zona de Riesgo:',
              name: zoneFieldName,
            }}
            inputNumberProps={{ disabled: true }}
          />
        </ColSize>
        <ColSize size={25}>
          <InputForm
            formProps={{
              label: 'R.U.T.A',
              name: `rutaNumber-tab-${tabKey}`,
            }}
          />
        </ColSize>
        <ColSize size={25}>
          <InputCurrencyForm
            formProps={{
              label: 'Suma:',
              name: infoValueFieldName,
            }}
            inputCurrencyProps={{ disabled: true }}
          />
        </ColSize>
        <ColSize size={25}>
          <InputForm
            formProps={{
              label: 'Patente:',
              name: `patent-tab-${tabKey}`,
              rules: validateForm.generateRules({
                label: 'patente',
                rules: ['required'],
              }),
              initialValue: patentValue ?? undefined,
            }}
            inputProps={{
              disabled: !!patentValue,
            }}
          />
        </ColSize>
        <ColSize size={25}>
          <InputForm
            formProps={{
              label: 'Número de Motor:',
              name: `engineNumber-tab-${tabKey}`,
              rules: validateForm.generateRules({
                label: 'número de motor',
                rules: ['required', 'minLength:4'],
              }),
              initialValue: engineNumberValue ?? undefined,
            }}
            inputProps={{
              disabled: !!engineNumberValue,
            }}
          />
        </ColSize>
        <ColSize size={25}>
        <InputForm
          formProps={{
            label: 'Número de Chasis:',
            name: `chassisNumber-tab-${tabKey}`,
            rules: validateForm.generateRules({
              label: 'número de chasis',
              rules: ['required', 'minLength:6'],
            }),
            initialValue: chassisNumberValue ?? undefined,
          }}
          inputProps={{
            disabled: !!chassisNumberValue,
          }}
        />
        </ColSize>
        <ColSize size={25}>
          <InputForm
            formProps={{
              label: 'Póliza C.I.:',
              name: `CIPolicy-tab-${tabKey}`,
            }}
          />
        </ColSize>
        <ColSize size={25}>
          <SelectForm
            formProps={{
              label: 'Acreedor:',
              name: `creditor-tab-${tabKey}`,
            }}
            selectProps={{
              options: collateralCreditors,
              disabled: !collateralCreditors.length,
            }}
          />
        </ColSize>
      </RowSize>
    </SectionQuotation>
  );
};
